import React from "react";
import { customSanitize } from "../../../utils/customSanitize";
import {
  ExtendedLink,
  ExtendedLinkType,
} from "@/components/ExtededLink/ExtendedLink";
import { InfoBoxElementType } from "../InfoBoxesModule";
import {
  IconGridOptionsType,
  InfoboxesOptionsType,
} from "@/types/ModuleOptionsType";
import styles from "../InfoBoxesModule.module.scss";
import { ImageType, LinkType } from "@/types/baseTypes";
import { getItemAttributes } from "@/components/_primitives/utils/getItemAttributes";
import { Breakpoint } from "@aleksundshantu/aunds-utils-react/layout";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { Button } from "@/components/Button/Button";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { getBoxPaddingVars } from "@/utils/getResponsiveCssVars";

interface InfoBoxProps {
  element: InfoBoxElementType;
  extraClass: string;
  infoBoxOptions: InfoboxesOptionsType | IconGridOptionsType;
  itemWidth?: Record<Breakpoint, string>;
  show_cardbox: boolean;
  show_divider: boolean;
  heading_level: number;
}

export type IconSizeType = {
  mobile: number | "";
  tablet: number | "";
  desktop: number | "";
  fullhd: number | "";
};

const getIconSize = (icon_size: IconSizeType) => {
  return {
    "--infobox-icon-size-mobile": `${icon_size.mobile}rem`,
    "--infobox-icon-size-tablet": `${icon_size.tablet}rem`,
    "--infobox-icon-size-desktop": `${icon_size.desktop}rem`,
    "--infobox-icon-size-fullhd": `${icon_size.fullhd}rem`,
  };
};

export const InfoBox = ({
  element,
  extraClass,
  infoBoxOptions,
  itemWidth,
  show_cardbox,
  show_divider,
  heading_level,
}: InfoBoxProps) => {
  //In Backend we can get both true at the same time, so show_cardbox has priority
  const effectiveShowCardbox = show_cardbox;
  const effectiveShowDivider = show_cardbox ? false : show_divider;

  const { show_extended_link, button_type, box } = infoBoxOptions ?? {};
  const icon_size =
    "icon_size" in infoBoxOptions ? infoBoxOptions.icon_size : null;
  const { title, infobox, link, link_extended, content } = element;
  const {
    // show_card_box: show_box,
    show_border_radius,
    show_box_shadow,
    box_padding,
  } = box;

  const isCentered = extraClass?.includes("has-text-centered");
  const attr = getItemAttributes({
    itemWidth: itemWidth,
    className: `column ${styles.infobox} ${
      !effectiveShowCardbox && effectiveShowDivider ? styles["has-divider"] : ""
    }`,
    itemAttrFor: "infobox",
  });

  const showBowClasses =
    effectiveShowCardbox && !effectiveShowDivider
      ? `${effectiveShowCardbox ? "is-box h-100" : ""} ${
          show_border_radius ? "has-border-radius" : ""
        } ${show_box_shadow ? "has-box-shadow" : ""}`
      : "";

  return (
    <div
      // className={`column ${styles.infobox}`}
      {...attr}
      style={{
        ...attr.style,
        ...(getBoxPaddingVars(box_padding ?? null) as React.CSSProperties),
        ...(icon_size && (getIconSize(icon_size) as React.CSSProperties)),
      }}
    >
      <div
        className={`${showBowClasses} ${extraClass} is-flex ${styles["box-wrapper"]}`}
      >
        {element.svg && (
          <div className={styles["infobox-icon"]}>
            <CustomSVG
              src={element.svg.url}
              wrapper="svg"
              className="is-flex"
              width={"100%"}
              height={"100%"}
              role="img"
              aria-label={`${(element.svg as ImageType).title} icon`}
            />
          </div>
        )}
        <div className="">
          {element?.tagline && (
            <p className="infobox-tagline">{element.tagline}</p>
          )}
          {title &&
            (button_type !== "link-in-title" ||
              !checkLinkAvailable(
                show_extended_link ? link_extended : link,
                show_extended_link,
              )) && (
              <p
                role="heading"
                aria-level={heading_level || 3}
                className="infobox-title is-block"
                dangerouslySetInnerHTML={{ __html: customSanitize(title) }}
              ></p>
            )}
          {title &&
            !show_extended_link &&
            link &&
            link.url &&
            button_type === "link-in-title" && (
              <Button
                role="button"
                className={`mb-0 ${isCentered ? "mx-auto" : ""}`}
                type={"secondary"}
                href={link.url}
                target={link.target === "" ? "_self" : link.target}
                aria-label={link.aria_label}
              >
                {link.title}
              </Button>
            )}
          {title &&
            button_type === "link-in-title" &&
            show_extended_link &&
            link_extended && (
              <ExtendedLink
                extendedLink={replaceExtendedLinkText(title, link_extended)}
                btnType={"secondary"}
                extraClass={`${isCentered ? "mx-auto" : ""} mt-0 infobox-title`}
              />
            )}
          {(infobox || content) && (
            <ContentTag
              extraClass={`infobox-text`}
              content={infobox || content || ""}
            />
          )}
          {/* TODO: implement link-in-tile */}
          {!show_extended_link &&
            link &&
            link.url &&
            button_type !== "link-in-title" && (
              <Button
                role="button"
                className={`mb-0 ${isCentered ? "mx-auto" : ""}`}
                type={button_type}
                href={link.url}
                target={link.target === "" ? "_self" : link.target}
                aria-label={link.aria_label}
              >
                {link.title}
              </Button>
            )}

          {show_extended_link &&
            link_extended &&
            button_type !== "link-in-title" && (
              <ExtendedLink
                extendedLink={link_extended}
                btnType={button_type}
                extraClass={`${isCentered ? "mx-auto" : ""}`}
              />
            )}
        </div>
      </div>
    </div>
  );
};

function replaceExtendedLinkText(
  text: string,
  extendedLink: ExtendedLinkType,
): ExtendedLinkType {
  const newExtendedLink = { ...extendedLink };
  const { type } = extendedLink;
  switch (type) {
    case "email":
      newExtendedLink.email.link_text = text;
      break;
    case "download":
      newExtendedLink.download.link_text = text;
      break;
    case "link":
      if (newExtendedLink.link) {
        newExtendedLink.link.title = text;
      }
      break;
    case "popup":
      newExtendedLink.pop_up.link_text = text;
      break;
  }
  return newExtendedLink;
}

function checkLinkAvailable(
  link: LinkType | ExtendedLinkType | false,
  show_extended_link: boolean,
): boolean {
  if (!link) return false;
  if (show_extended_link) {
    const extendedLink = link as ExtendedLinkType;
    const { type } = extendedLink;
    switch (type) {
      case "email":
        return !!extendedLink.email?.link_target;
      case "download":
        return !!extendedLink.download?.link_target;
      case "link":
        return !!(extendedLink.link && extendedLink.link?.url);
      case "popup":
        return !!extendedLink.pop_up;
      default:
        return false;
    }
  } else {
    const normalLink = link as LinkType;
    return !!(normalLink && normalLink.url);
  }
}
